import openApiSchema30 from '../openapiv3.schema.json';
import openApiSchema31 from '../openapiv31.schema.json';
import Ajv2020 from 'ajv/dist/2020';
import AjvDraft04 from 'ajv-draft-04';

// Dual-schema dispatch. OpenAPI 3.0 uses JSON Schema draft-4 semantics so we
// keep ajv-draft-04 for those specs. OpenAPI 3.1 is built on JSON Schema
// 2020-12, which is native to ajv v8's Ajv2020 class. We compile each
// validator once, then route at validate() time based on the spec's
// openapi version string.
//
// ajv v8 error format is the same for both: { instancePath, keyword, ...}
// so all downstream error-filtering code (errorsForModel/Interface,
// lint-view display) doesn't care which validator ran.

const ajv30 = new AjvDraft04({
    allErrors: true,
    strict: false,
    logger: false,
});
const compiled30 = ajv30.compile(openApiSchema30);

// 3.1 Schema Objects use JSON Schema 2020-12 semantics, published as a
// separate "dialect" file (https://spec.openapis.org/oas/3.1/dialect/base).
// Without loading that dialect into ajv, the main 3.1 metaschema flags
// every valid `type` / `properties` / etc. inside a Schema Object as
// "unevaluatedProperties". We prune unevaluatedProperties:false from the
// embedded schema so structural validation proceeds without thousands of
// false positives. True 2020-12 validation of Schema Objects can be
// wired in a follow-up phase by registering the dialect. For now the 3.1
// validator catches top-level shape errors (missing info, bad webhooks
// structure, etc.) which is what the UI needs.
function relaxUnevaluated(schema) {
    if (schema == null || typeof schema !== 'object') return schema;
    if (Array.isArray(schema)) return schema.map(relaxUnevaluated);
    const out = {};
    for (const k of Object.keys(schema)) {
        if (k === 'unevaluatedProperties') continue;
        out[k] = relaxUnevaluated(schema[k]);
    }
    return out;
}

const ajv31 = new Ajv2020({
    allErrors: true,
    strict: false,
    logger: false,
});
const compiled31 = ajv31.compile(relaxUnevaluated(openApiSchema31));

// Pick a validator for the spec. Returns the 3.0 validator by default so
// specs with missing / malformed `openapi` field still get useful errors.
function pickValidator(spec) {
    const v = spec && typeof spec.openapi === 'string' ? spec.openapi : '';
    if (v.startsWith('3.1')) return compiled31;
    return compiled30;
}

export default function validator(spec, {dispatch}) {
    const validate = pickValidator(spec);
    const result = validate(spec);
    dispatch({type: 'validate-spec-error', errors: result ? [] : validate.errors || []});
    return spec;
}

function encodeJsonPointerSegment(key) {
    return String(key).replace(/~/g, '~0').replace(/\//g, '~1');
}

export function errorsForModel(errors, key) {
    const needle = `/components/schemas/${encodeJsonPointerSegment(key)}`;
    return (errors || []).filter((msg) => (msg.instancePath || '').indexOf(needle) !== -1).slice(0, 300);
}

export function errorsForInterface(errors, key) {
    const needle = `/paths/${encodeJsonPointerSegment(key)}`;
    return (errors || []).filter((msg) => (msg.instancePath || '').indexOf(needle) !== -1).slice(0, 300);
}
